/**
 * AP3 (#5) — Workspace-Einstellungen: Umbenennen + Mitglieder/Rollen verwalten.
 * Backend war bereits vorhanden (PATCH /workspaces/:ws für den Namen; GET/PUT/
 * DELETE /members; POST /invitations für neue Mitglieder — kein Force-Join,
 * D-142). owner/admin dürfen ändern; sonst read-only. Slug bleibt fix.
 */

import { useCallback, useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { WsMember } from "../api";

const ROLES = ["owner", "admin", "member", "viewer"] as const;
const INVITE_ROLES = ["admin", "member", "viewer"] as const;

export const RyfeWorkspaceSettings = () => {
  const { workspace, refresh } = useRyfe();
  const wsId = workspace?.id;
  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin";

  const [name, setName] = useState(workspace?.name || "");
  const [members, setMembers] = useState<WsMember[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] =
    useState<typeof INVITE_ROLES[number]>("member");
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const loadMembers = useCallback(() => {
    if (!wsId) {
      return;
    }
    ryfeApi
      .listMembers(wsId)
      .then((r) => setMembers(r.members))
      .catch((e) => setError((e as Error).message));
  }, [wsId]);

  useEffect(() => {
    setName(workspace?.name || "");
    loadMembers();
  }, [workspace?.name, loadMembers]);

  if (!wsId) {
    return (
      <div className="ryfe-page">
        <h1 className="ryfe-page__title">Workspace-Einstellungen</h1>
        <p className="ryfe-muted" style={{ padding: 0 }}>
          Kein Workspace aktiv.
        </p>
      </div>
    );
  }

  const doRename = async () => {
    setError(null);
    setMsg(null);
    try {
      await ryfeApi.renameWorkspace(wsId, name.trim());
      await refresh();
      setMsg("Name gespeichert.");
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const changeRole = async (m: WsMember, role: string) => {
    setError(null);
    try {
      await ryfeApi.setMemberRole(wsId, m.email, role as typeof ROLES[number]);
      loadMembers();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (m: WsMember) => {
    if (!window.confirm(`„${m.email}" aus dem Workspace entfernen?`)) {
      return;
    }
    setError(null);
    try {
      await ryfeApi.removeMember(wsId, m.id);
      loadMembers();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) {
      return;
    }
    setError(null);
    setMsg(null);
    setInviteToken(null);
    try {
      const r = await ryfeApi.inviteMember(
        wsId,
        inviteEmail.trim(),
        inviteRole,
      );
      setInviteEmail("");
      setMsg("Einladung erstellt.");
      // Übergangslösung, solange kein E-Mail-Versand: den signup_token zeigen.
      if (r.signup_token) {
        setInviteToken(r.signup_token);
      }
      loadMembers();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">Workspace-Einstellungen</h1>

      {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}
      {msg && <div className="ryfe-notice">{msg}</div>}

      {/* Umbenennen */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">Name</h2>
        <label className="ryfe-field">
          <span>Workspace-Name</span>
          <input
            value={name}
            disabled={!isAdmin}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {isAdmin && (
          <div className="ryfe-modal__actions">
            <span className="ryfe-topbar__spacer" />
            <button
              className="ryfe-btn ryfe-btn--primary"
              disabled={!name.trim() || name.trim() === workspace?.name}
              onClick={() => void doRename()}
            >
              Speichern
            </button>
          </div>
        )}
      </div>

      {/* Mitglieder */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">Mitglieder</h2>
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>E-Mail</th>
                <th>Rolle</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="ryfe-row">
                  <td>{m.email}</td>
                  <td>
                    {isAdmin ? (
                      <select
                        value={m.role}
                        onChange={(e) => void changeRole(m, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="ryfe-muted">{m.role}</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      <button
                        className="ryfe-linkish ryfe-context__danger"
                        onClick={() => void remove(m)}
                      >
                        Entfernen
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Einladen (owner/admin) */}
      {isAdmin && (
        <div className="ryfe-panel">
          <h2 className="ryfe-panel__title">Mitglied einladen</h2>
          <p className="ryfe-muted" style={{ padding: 0 }}>
            Neue Mitglieder treten per Einladung bei (kein Force-Join). Die
            Person nimmt die Einladung an; solange kein E-Mail-Versand aktiv
            ist, kann der Einladungs-Token weitergegeben werden.
          </p>
          <div className="ryfe-rrule__row">
            <input
              type="email"
              placeholder="name@firma.de"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <select
              value={inviteRole}
              onChange={(e) =>
                setInviteRole(e.target.value as typeof INVITE_ROLES[number])
              }
            >
              {INVITE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button
              className="ryfe-btn ryfe-btn--primary"
              disabled={!inviteEmail.trim()}
              onClick={() => void invite()}
            >
              Einladen
            </button>
          </div>
          {inviteToken && (
            <p className="ryfe-muted" style={{ padding: 0 }}>
              Einladungs-Token: <code>{inviteToken}</code>
            </p>
          )}
        </div>
      )}
    </div>
  );
};
